import React, { useEffect, useMemo, useState } from 'react';

import useOrgSettingsStore from '../../stores/orgSettingsStore';

/**
 * SittingCard — one sitting's inline display with context-aware actions.
 *
 * Props:
 *   sitting         — SittingSummary or SittingResponse (accepts either; reads
 *                     only the fields both have in common)
 *   viewer          — { memberId: string, role: 'DOCTOR'|'NURSE'|'RECEPTIONIST'|'ADMIN' }
 *   onAction        — async ({action, sitting, payload?}) => void
 *                     action ∈ {
 *                       'collect_payment', 'execute', 'complete',
 *                       'approve', 'reject', 'cancel', 'reassign',
 *                     }
 *                     Parent owns the modal flow for actions that need input
 *                     (collect_payment, reject, cancel, reassign).
 *   dense           — bool, slimmer rendering for queue lists (default false)
 *
 * Action gating rules (UI hint — backend is authoritative):
 *   - Collect Payment: awaiting_payment, and caller has COLLECT_PAYMENT (DOCTOR/RECEPTIONIST/ADMIN)
 *   - Start         : payment_cleared | scheduled (today), and caller is assignee
 *   - Complete      : in_progress, and caller is assignee
 *   - Approve/Reject: pending_approval, and caller is approver
 *   - Reassign / Cancel: DOCTOR/ADMIN, non-terminal status
 */
const STATUS_META = {
  awaiting_payment:  { label: 'Awaiting payment',   color: 'bg-amber-100 text-amber-900 border-amber-300' },
  payment_cleared:   { label: 'Ready to start',     color: 'bg-teal-100 text-teal-900 border-teal-300' },
  scheduled:         { label: 'Scheduled',          color: 'bg-sky-100 text-sky-900 border-sky-300' },
  in_progress:       { label: 'In progress',        color: 'bg-blue-100 text-blue-900 border-blue-300' },
  pending_approval:  { label: 'Awaiting approval',  color: 'bg-purple-100 text-purple-900 border-purple-300' },
  completed:         { label: 'Completed',          color: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  paid:              { label: 'Paid',               color: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  cancelled:         { label: 'Cancelled',          color: 'bg-slate-100 text-slate-600 border-slate-300 line-through' },
};

function formatCurrency(amount) {
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatScheduledFor(value) {
  if (!value) return null;
  // 'YYYY-MM-DDTHH:MM:SS' → 'Apr 25 · 2:00 PM'
  try {
    const [datePart, timePart] = value.split('T');
    const d = new Date(`${datePart}T${(timePart || '00:00:00').slice(0, 8)}`);
    return d.toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short',
    }) + (timePart ? ` · ${d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })}` : '');
  } catch {
    return value;
  }
}

/**
 * Day-relative label for a sitting's scheduled date.
 *   - today:    "Today"
 *   - tomorrow: "Tomorrow"
 *   - past:     "Overdue · <date>"
 *   - later:    "<date>" (e.g. "30 Apr")
 * Time portion (if any) is appended as " · HH:MM AM/PM".
 * Returns `null` when no date can be derived.
 */
function describeWhen(sitting) {
  const raw = sitting?.scheduled_for;
  const timingMode = sitting?.timing_mode;
  if (!raw) {
    // Unscheduled sittings — fall back to timing_mode hint when set.
    if (timingMode === 'later_today') return { label: 'Later today', tone: 'today' };
    if (timingMode === 'now') return { label: 'Today', tone: 'today' };
    return null;
  }
  try {
    const [datePart, timePart] = String(raw).split('T');
    const target = new Date(`${datePart}T00:00:00`);
    const now = new Date();
    const today = new Date(`${now.toISOString().slice(0, 10)}T00:00:00`);
    const days = Math.round((target.getTime() - today.getTime()) / 86400000);
    const clock = timePart && timePart !== '00:00:00'
      ? ` · ${new Date(`${datePart}T${timePart.slice(0, 8)}`).toLocaleTimeString(
          'en-IN', { hour: 'numeric', minute: '2-digit', hour12: true },
        )}`
      : '';
    let label;
    let tone;
    if (days < 0) {
      label = `Overdue · ${target.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
      tone = 'overdue';
    } else if (days === 0) {
      label = `Today${clock}`;
      tone = 'today';
    } else if (days === 1) {
      label = `Tomorrow${clock}`;
      tone = 'upcoming';
    } else {
      label = `${target.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}${clock}`;
      tone = 'upcoming';
    }
    return { label, tone };
  } catch {
    return null;
  }
}

const WHEN_TONE_CLASS = {
  today:    'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700',
  upcoming: 'bg-sky-100 text-sky-900 border-sky-300 dark:bg-sky-900/40 dark:text-sky-200 dark:border-sky-700',
  overdue:  'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700',
};


function SittingCard({ sitting, viewer, onAction, dense = false, planTitle = null, readOnly = false }) {
  const [busy, setBusy] = useState(null); // action key currently in flight

  const status = sitting.status;
  const assignedToMemberId =
    sitting.assigned_to?.member_id
    || sitting.assigned_to_member_id
    || null;
  const assignedToName =
    sitting.assigned_to_name
    || sitting.assigned_to?.name
    || '';
  const procedureName =
    sitting.procedure_name
    || sitting.procedure_snapshot?.name
    || '(procedure)';

  const meta = STATUS_META[status] || { label: status, color: 'bg-slate-100 text-slate-800 border-slate-300' };

  const isAssignee = viewer?.memberId && assignedToMemberId === viewer.memberId;
  const isApprover = viewer?.memberId && sitting.approver_member_id === viewer.memberId;
  const isAdmin = (viewer?.role || '').toUpperCase() === 'ADMIN';
  const isDoctor = (viewer?.role || '').toUpperCase() === 'DOCTOR';
  const isReceptionist = (viewer?.role || '').toUpperCase() === 'RECEPTIONIST';

  // Phase 1d — viewer is the visit's primary doctor but has delegated this
  // sitting to someone else. Show a "Delegated to {name}" pill and hide
  // Start / Complete (not the viewer's job to execute). Keep Reassign /
  // Cancel / Approve / Review / Receipt / Open visit available so the
  // senior can still intervene.
  const isVisitDoctor =
    viewer?.memberId && sitting.visit_doctor_member_id === viewer.memberId;
  const isDelegatedByMe = isVisitDoctor && !isAssignee;

  // Doctor-collect policy. When the clinic hasn't enabled doctor-collect,
  // a doctor sees a disabled Collect button + a "clear at Reception" note.
  const doctorCanCollect = useOrgSettingsStore((s) => s.doctorCanCollectPayment);
  const fetchOrgSettings = useOrgSettingsStore((s) => s.fetchProcedurePaymentSettings);
  useEffect(() => { fetchOrgSettings(); }, [fetchOrgSettings]);

  // Which primary action to surface, given the viewer + status.
  // R5 (owner) — readOnly (Manage Visits Details for RECEPTIONIST) suppresses
  // every action button, not just the write-shaped ones: simplest to reason
  // about and verify, and matches "no add/edit/delete/collect" literally.
  const primary = useMemo(() => {
    if (readOnly) return null;
    if (status === 'awaiting_payment' && (isReceptionist || isAdmin)) {
      return { key: 'collect_payment', label: 'Collect payment', intent: 'primary' };
    }
    if (status === 'awaiting_payment' && isDoctor) {
      if (doctorCanCollect) {
        return { key: 'collect_payment', label: 'Collect payment', intent: 'primary' };
      }
      // Gated off: disabled button + Reception message.
      return {
        key: 'collect_payment',
        label: 'Collect payment',
        intent: 'primary',
        disabled: true,
        note: 'To start procedure, patient needs to clear payment on Reception.',
      };
    }
    if (isDelegatedByMe) return null;  // senior isn't the executor
    if ((status === 'payment_cleared' || status === 'scheduled') && (isAssignee || isAdmin)) {
      return { key: 'execute', label: 'Start', intent: 'primary' };
    }
    if (status === 'in_progress' && (isAssignee || isAdmin)) {
      return { key: 'complete', label: 'Mark complete', intent: 'primary' };
    }
    if (status === 'pending_approval' && (isApprover || isAdmin)) {
      return { key: 'approve', label: 'Approve', intent: 'primary' };
    }
    return null;
  }, [readOnly, status, isDoctor, isReceptionist, isAdmin, isAssignee, isApprover, isDelegatedByMe, doctorCanCollect]);

  const secondaryActions = useMemo(() => {
    const out = [];
    if (readOnly) return out;
    if (status === 'pending_approval' && (isApprover || isAdmin)) {
      // Phase 1b — Review… opens the bundle modal for in-depth sign-off.
      // The primary Approve button stays on the card (fast-approve path).
      out.push({ key: 'review', label: 'Review…', intent: 'ghost' });
      out.push({ key: 'reject', label: 'Reject', intent: 'warning' });
    }
    const terminal = ['completed', 'paid', 'cancelled'].includes(status);
    // Phase 1e — "📝 Note" for the current assignee on non-terminal
    // sittings. Same role gate as the visit-page inline note section
    // (Phase 1b META_WRITE_PERMISSIONS for PROCEDURE_NOTE). Admins can
    // always write. Approver-only viewers who aren't the assignee don't
    // get the button — they read notes via the approval dialog / visit
    // page.
    const hasVisitId = !!(sitting.visit_id);
    if (hasVisitId && !terminal && (isAssignee || isAdmin)) {
      out.push({ key: 'write_note', label: '📝 Note', intent: 'ghost' });
    }
    if (!terminal && (isDoctor || isAdmin)) {
      out.push({ key: 'reassign', label: 'Reassign', intent: 'ghost' });
      out.push({ key: 'cancel', label: 'Cancel', intent: 'danger' });
    }
    // Phase 2.7 — "Exclude from plan" on optional sittings that came from
    // a workflow master. Receptionist + doctor + admin can exclude; nurse
    // cannot. Mandatory sittings + terminal sittings + non-workflow sittings
    // don't get the button at all.
    const isOptionalWorkflow = (
      sitting.source_step_id
      && sitting.is_mandatory === false
    );
    if (
      !terminal
      && isOptionalWorkflow
      && (isDoctor || isAdmin || isReceptionist)
    ) {
      out.push({
        key: 'exclude_from_plan',
        label: 'Exclude (optional)',
        intent: 'ghost',
      });
    }
    // Phase 1e — "See visit team →" replaces the old "Open visit" button.
    // Opens the visit page's team panel so the viewer can see who else
    // is on the visit and what's done. Gated by `hasVisitId + viewer is
    // on this visit` (assignee OR visit doctor OR admin/primary doctor
    // in that org).
    if (hasVisitId && (isAssignee || isAdmin || isDoctor)) {
      out.push({ key: 'open_visit', label: 'See visit team →', intent: 'ghost' });
    }
    // Phase 1f — "🖨 Print assignments" for the front desk. Shows the
    // patient-identity + per-procedure + assignee table. Only meaningful
    // when the sitting is on a bound visit.
    if (hasVisitId && (isReceptionist || isAdmin)) {
      out.push({
        key: 'print_assignments',
        label: '🖨 Print assignments',
        intent: 'ghost',
      });
    }
    // Phase 1c — Receipt action surfaces when payment has cleared, i.e.
    // the sitting has moved past awaiting_payment. Endpoint is idempotent
    // so the same button doubles as Reprint for terminal paid sittings.
    // Label says "Reprint receipt" once the sitting is terminal (signals
    // intent); otherwise "Receipt" for mid-flow status like
    // payment_cleared / in_progress.
    const paymentCleared = status !== 'awaiting_payment';
    if (paymentCleared && (isDoctor || isReceptionist || isAdmin)) {
      out.push({
        key: 'receipt',
        label: terminal ? '🖨 Reprint receipt' : '📄 Receipt',
        intent: 'ghost',
      });
    }
    return out;
  }, [readOnly, status, isApprover, isAdmin, isDoctor, isReceptionist, isAssignee, sitting.visit_id, sitting.source_step_id, sitting.is_mandatory]);

  const handle = async (actionKey) => {
    if (busy || !onAction) return;
    setBusy(actionKey);
    try {
      await onAction({ action: actionKey, sitting });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`sitting-card border border-slate-200 dark:border-slate-700 rounded-lg ${dense ? 'p-3' : 'p-4'} bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <span className={`inline-block text-xs font-medium px-2 py-0.5 border rounded-full ${meta.color}`}>
              {meta.label}
            </span>
            {/* Phase 2.5 — day-relative scheduling badge. Shows Today /
                Tomorrow / absolute date so doctors + receptionists see
                WHEN the sitting is due at a glance. */}
            {(() => {
              const when = describeWhen(sitting);
              if (!when) return null;
              const tone = WHEN_TONE_CLASS[when.tone] || WHEN_TONE_CLASS.upcoming;
              return (
                <span
                  className={`inline-block text-xs font-medium px-2 py-0.5 border rounded-full ${tone}`}
                  title="Scheduled"
                >
                  📅 {when.label}
                </span>
              );
            })()}
            {sitting.payment_timing === 'after' && (
              <span className="inline-block text-xs font-medium px-2 py-0.5 border rounded-full bg-yellow-50 text-yellow-800 border-yellow-300">
                Pay-after
              </span>
            )}
            {sitting.standing && (
              <span
                className="inline-block text-xs font-medium px-2 py-0.5 border rounded-full bg-fuchsia-50 text-fuchsia-800 border-fuchsia-300 dark:bg-fuchsia-900/40 dark:text-fuchsia-200 dark:border-fuchsia-700"
                title={sitting.expires_at ? `Standing order — repeats until ${sitting.expires_at}` : 'Standing (repeatable) order'}
              >
                Standing{sitting.expires_at ? ` · till ${sitting.expires_at}` : ''}
              </span>
            )}
            {sitting.requires_approval && (
              <span className="inline-block text-xs font-medium px-2 py-0.5 border rounded-full bg-purple-50 text-purple-800 border-purple-300">
                Needs approval
              </span>
            )}
            {sitting.source === 'NURSE_WALKIN' && (
              <span className="inline-block text-xs font-medium px-2 py-0.5 border rounded-full bg-blue-50 text-blue-800 border-blue-300">
                Nurse walk-in
              </span>
            )}
            {/* Phase 2.8 — surface the parent PatientPlan when the
                sitting was materialised from one. Lets receptionists +
                assignees jump to the plan page from any sitting card. */}
            {sitting.source_plan_id && (
              <a
                href={`/org/patient-plans/${sitting.source_plan_id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-block text-xs font-medium px-2 py-0.5 border rounded-full bg-indigo-50 text-indigo-800 border-indigo-300 hover:bg-indigo-100"
                title="Open the parent patient plan"
              >
                📋 Plan
              </a>
            )}
            {sitting.source === 'RECEPTIONIST_WALKIN' && (
              <span className="inline-block text-xs font-medium px-2 py-0.5 border rounded-full bg-blue-50 text-blue-800 border-blue-300">
                Walk-in
              </span>
            )}
            {isDelegatedByMe && (
              <span
                className="inline-block text-xs font-medium px-2 py-0.5 border rounded-full bg-indigo-50 text-indigo-800 border-indigo-300"
                title="You ordered this procedure and delegated it. You stay in the loop but the assignee executes."
              >
                Delegated to {assignedToName || assignedToMemberId}
              </span>
            )}
            {/* Phase 2.5 — show which treatment plan this sitting belongs
                to. Only rendered when the parent resolved a non-empty
                title (single-visit plans have no title). */}
            {planTitle && (
              <span
                className="inline-block text-xs font-semibold px-2 py-0.5 border rounded-full bg-indigo-100 text-indigo-900 border-indigo-400 dark:bg-indigo-900/50 dark:text-indigo-100 dark:border-indigo-700"
                title={`Part of treatment plan: ${planTitle}`}
              >
                📋 {planTitle}
              </span>
            )}
          </div>
          {/* Phase 1e — patient-first row. Anchors the card around the
              patient so a nurse knows *whose* procedure she's about to
              do. Falls back to the procedure name when patient_name
              hasn't been denormed yet (legacy sitting rows). */}
          {(sitting.patient_name || sitting.queue_number != null) && (
            <div className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-baseline gap-2">
              <span className="truncate">
                {sitting.patient_name || procedureName}
              </span>
              {sitting.queue_number != null && (
                <span className="text-xs font-semibold text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700 rounded px-2 py-0.5">
                  #Q-{sitting.queue_number}
                </span>
              )}
            </div>
          )}
          {/* Procedure name — secondary when we have a patient name,
              otherwise primary. */}
          <div
            className={
              sitting.patient_name
                ? 'text-sm text-slate-700 dark:text-slate-300 truncate'
                : 'text-base font-semibold truncate'
            }
          >
            {procedureName}
          </div>
          {/* Show the tokens this procedure applies to, inline below the
              procedure name, so a doctor on the next visit can see "RCT
              was on tooth 16" without opening the chart. */}
          {Array.isArray(sitting.tooth_numbers) && sitting.tooth_numbers.length > 0 && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-slate-700 dark:text-slate-300"
                 title={`Applied to: ${sitting.tooth_numbers.join(', ')}`}>
              <span aria-hidden="true">🦷</span>
              {sitting.tooth_numbers.slice(0, 6).map((t) => (
                <span
                  key={t}
                  className="px-1.5 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 font-semibold"
                >
                  {t.startsWith('gum:') ? `🩸${t.slice(4)}` : t}
                </span>
              ))}
              {sitting.tooth_numbers.length > 6 && (
                <span className="opacity-70">+{sitting.tooth_numbers.length - 6}</span>
              )}
            </div>
          )}
          {/* Context row: visit doctor + current assignee + cost/schedule. */}
          <div className="mt-1 text-xs text-slate-600 dark:text-slate-400 flex flex-wrap gap-x-3 gap-y-1">
            {sitting.visit_doctor_name && (
              <span>
                <span className="text-slate-500 dark:text-slate-500">Visit:</span>{' '}
                {sitting.visit_doctor_name}
              </span>
            )}
            {assignedToMemberId && (
              <span title={assignedToMemberId}>
                <span className="text-slate-500 dark:text-slate-500">Assignee:</span>{' '}
                {(assignedToName || assignedToMemberId)}
                {isAssignee && (
                  <span className="ml-1 text-teal-700 dark:text-teal-300 font-medium">
                    (you)
                  </span>
                )}
              </span>
            )}
            <span>{formatCurrency(sitting.final_cost)}</span>
            {sitting.scheduled_for && (
              <span>⏱ {formatScheduledFor(sitting.scheduled_for)}</span>
            )}
          </div>
        </div>

        {/* Primary action */}
        {primary && (
          <button
            type="button"
            onClick={() => handle(primary.key)}
            disabled={!!busy || !!primary.disabled}
            title={primary.note || undefined}
            className={`shrink-0 px-3 py-1.5 rounded-md text-sm font-medium transition
              ${primary.intent === 'primary'
                ? 'bg-teal-600 hover:bg-teal-700 text-white disabled:bg-teal-300'
                : 'bg-slate-200 hover:bg-slate-300 text-slate-900 disabled:opacity-50'}
              ${primary.disabled ? 'cursor-not-allowed' : ''}
            `}
          >
            {busy === primary.key ? '…' : primary.label}
          </button>
        )}
      </div>

      {/* Doctor-collect gated note — patient must pay at Reception first. */}
      {primary?.disabled && primary?.note && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          {primary.note}
        </p>
      )}

      {/* Secondary actions */}
      {secondaryActions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {secondaryActions.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => handle(a.key)}
              disabled={!!busy}
              className={`px-2.5 py-1 rounded text-xs font-medium border
                ${a.intent === 'warning' ? 'border-amber-300 text-amber-800 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30' : ''}
                ${a.intent === 'danger'  ? 'border-red-300 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30' : ''}
                ${a.intent === 'ghost'   ? 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700' : ''}
                disabled:opacity-50
              `}
            >
              {busy === a.key ? '…' : a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SittingCard;
